import { useEffect, useState } from 'react';
import { traiterFile, reparerSynchronisation, renvoyerElement } from '../offline/syncQueue.ts';
import type { RapportReparation } from '../offline/syncQueue.ts';
import { traiterFileCpn } from '../offline/syncQueueCpn.ts';
import { synchroniser, lireEtatModules, listerIncidents, marquerIncidentsVus, surChangement } from '../offline/dbModules.ts';
import type { Incident } from '../offline/dbModules.ts';
import { listerParPeriode } from '../offline/db.ts';
import type { LocalConsultation } from '../offline/db.ts';
import type { Periode } from './Accueil.tsx';

const LIBELLE_STATUT: Record<string, string> = {
  PENDING: '⚠ En attente',
  SYNCED: '✓ Synchronisé',
  ERROR: '⛔ Refusé / À vérifier',
  CONFLICT: '⚠ Conflit — version serveur conservée, à revoir',
};

export default function Synchronisation({ periode }: { periode: Periode | null }) {
  const [items, setItems] = useState<LocalConsultation[]>([]);
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [nbModules, setNbModules] = useState(lireEtatModules().nbAttente);
  const [nbRefusesModules, setNbRefusesModules] = useState(lireEtatModules().nbRefuses);
  const [raisonBlocage, setRaisonBlocage] = useState<string | null>(lireEtatModules().derniereErreur);
  const [reparationEnCours, setReparationEnCours] = useState(false);
  const [rapport, setRapport] = useState<RapportReparation | null>(null);

  useEffect(() => {
    const maj = () => { const e = lireEtatModules(); setNbModules(e.nbAttente); setNbRefusesModules(e.nbRefuses); setRaisonBlocage(e.derniereErreur); void listerIncidents().then(setIncidents); };
    maj(); const off = surChangement(maj); void marquerIncidentsVus();
    return off;
  }, []);

  async function rafraichir() {
    if (periode) setItems(await listerParPeriode(periode.id));
  }

  useEffect(() => {
    rafraichir();
    const onOnline = () => setEnLigne(true);
    const onOffline = () => setEnLigne(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const interval = setInterval(rafraichir, 5000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);

  /** « Réparer la synchronisation » : n'efface RIEN (ni sur l'appareil ni sur le serveur) — voir reparerSynchronisation. */
  async function reparer() {
    if (!periode || reparationEnCours) return;
    setReparationEnCours(true); setRapport(null);
    try { setRapport(await reparerSynchronisation(periode.id)); }
    finally { setReparationEnCours(false); await rafraichir(); }
  }

  if (!periode) return <p>Sélectionnez d'abord une période sur l'écran Accueil.</p>;

  return (
    <div>
      <h1>Synchronisation</h1>
      <p>État de la connexion : <strong>{enLigne ? 'En ligne' : 'Hors connexion'}</strong></p>
      {/* Envois STRICTEMENT séquentiels, y compris entre les files : jamais deux requêtes en parallèle (bas débit). */}
      <button onClick={async () => { await traiterFile({ reessayerRefuses: true }); await traiterFileCpn({ reessayerRefuses: true }); await synchroniser({ reessayerRefuses: true }); await rafraichir(); }} disabled={!enLigne}>Forcer la synchronisation</button>
      {/* Le bouton « Vider les saisies locales du mois » est retiré : il supprimait aussi les données du serveur (suppression logique) et
          pouvait faire perdre le travail du mois. Remplacé par « Réparer la synchronisation », qui n'efface jamais rien. */}
      <button className="secondaire" style={{ marginLeft: 8 }} onClick={reparer} disabled={!enLigne || reparationEnCours}>
        {reparationEnCours ? 'Réparation en cours…' : '🛠 Réparer la synchronisation'}
      </button>
      {rapport && (
        <div className="carte" role="status" style={{ marginTop: 12 }}>
          <h2>Rapport de réparation</h2>
          <p>{rapport.message}</p>
          <ul>
            <li>Envoyé au serveur : <strong>{rapport.envoyes}</strong> (dont <strong>{rapport.repares}</strong> saisie(s) qui étaient refusées)</li>
            <li>Fiches « fantômes » nettoyées : <strong>{rapport.orphelinsRetires}</strong></li>
            {rapport.patientsServeur !== null && <li>Patients du mois sur le serveur : <strong>{rapport.patientsServeur}</strong></li>}
            <li>Encore à vérifier : <strong>{rapport.bloques.length}</strong></li>
          </ul>
          {rapport.bloques.length > 0 && (
            <ul>{rapport.bloques.map((b) => <li key={b.localId}><b>{b.statut === 'ERREUR' ? '⛔ Refusé' : b.statut === 'CONFLIT' ? '⚠ Conflit' : '⏳ En attente'}</b> — {b.motif}</li>)}</ul>
          )}
          <p style={{ fontSize: '0.85rem', color: '#666' }}>« Garder » : ne touchez à rien, la saisie reste sur l'appareil. « Renvoyer » : bouton du tableau ci-dessous (saisies refusées).</p>
        </div>
      )}
      <p>Saisies des modules en attente d'envoi : <strong>{nbModules}</strong>{nbRefusesModules > 0 && <> · ⛔ Refusées / à vérifier : <strong>{nbRefusesModules}</strong></>}</p>
      {nbModules > 0 && raisonBlocage && <p role="alert" style={{ color: '#b00020' }}>⚠ Envoi des modules bloqué : {raisonBlocage}</p>}
      {incidents.length > 0 && (
        <div className="carte">
          <h2>À vérifier</h2>
          <ul>{incidents.slice(0, 20).map((i) => <li key={i.id}><b>{i.type === 'CONFLIT' ? 'Conflit' : 'Refusé'}</b> — {i.tableau ?? ''} {i.cle ?? ''} : {i.message}</li>)}</ul>
        </div>
      )}

      <table style={{ marginTop: 20 }}>
        <thead><tr><th>N° RCE</th><th>Action</th><th>Statut</th><th>Détail</th><th></th></tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.localId}>
              <td>{(i.payload as any).numeroRceBrut}</td>
              <td>{i.operation === 'CREATE' ? 'Création' : i.operation === 'DELETE' ? 'Suppression' : i.operation === 'DELETE_ALL' ? 'Suppression de tout le mois' : 'Modification'}</td>
              <td>{LIBELLE_STATUT[i.syncStatus]}</td>
              <td>{i.erreur ?? '—'}</td>
              <td>{i.syncStatus === 'ERROR' && <button className="secondaire" onClick={async () => { await renvoyerElement(i.localId); await rafraichir(); }} disabled={!enLigne}>Renvoyer</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <p>Aucune consultation locale pour cette période.</p>}
      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 20 }}>
        Les données saisies restent toujours sur cet appareil tant que le serveur n'a pas confirmé leur
        réception — elles ne sont jamais perdues en cas de coupure réseau.
      </p>
    </div>
  );
}
